import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../seo/routeMetadata';

const DEFAULT_BASE = 'https://amppredict.com';

/**
 * Remove ONLY the pre-rendered head tags (marked `data-ssr="1"` by the
 * postbuild script) so React 19's hoisted metadata becomes the single
 * source of truth after hydration.
 *
 * Prior versions of this helper removed ANY matching tag which nuked
 * React's own hoisted copies too and left <head> empty. The `data-ssr`
 * discriminator is the fix: React never adds that attribute, so its
 * tags are untouched.
 */
const cleanupStaticHead = () => {
  if (typeof document === 'undefined') return;
  const nodes = document.head.querySelectorAll('[data-ssr="1"]');
  nodes.forEach((n) => n.remove());
};

/**
 * Build a FAQPage JSON-LD node from a `[{q, a}]` array. Kept here so the
 * SPA render matches what `scripts/generate-static-pages.js` bakes into
 * the pre-rendered static HTML.
 */
const faqPageLd = (faq) => ({
  '@type': 'FAQPage',
  mainEntity: faq.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

/**
 * SEO/GEO head-tag manager using React 19 native metadata hoisting.
 * React 19 auto-hoists <title>, <meta>, <link>, and <script> to <head>,
 * deduplicating by name/property. Works with React Router SPAs out of the box.
 *
 * @param {string}  title         Page title. "AMP Predict" is auto-appended.
 * @param {string}  description   <=160 char summary.
 * @param {string}  keywords      Optional comma-separated keywords.
 * @param {string}  image         Absolute OG/Twitter image URL.
 * @param {string}  url           Canonical URL for this page.
 * @param {string}  type          OG type (website|article|product).
 * @param {boolean} noindex       If true, emits `noindex,nofollow` meta.
 * @param {object|object[]} jsonLd  Optional JSON-LD structured data object(s).
 */
const SEO = ({
  title = 'AMP Predict - Mock Event Contract Prediction Platform',
  description = 'AMPPredict lets you practice prediction markets with mock events: create markets, trade YES/NO contracts, and learn market mechanics—risk-free.',
  keywords = 'prediction market, event contracts, mock trading, practice trading, forecasting, AMP Predict',
  image = 'https://amppredict.com/og-image.png',
  url = DEFAULT_BASE,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title.includes('AMP Predict') ? title : `${title} | AMP Predict`;

  // Auto-resolve route-level jsonLd + FAQPage from the routeMetadata table
  // so every page that renders <SEO /> without an explicit jsonLd prop
  // still ships its full structured-data payload. Matches the static-HTML
  // generator's behavior for parity between SPA and pre-rendered output.
  const location = useLocation();
  const routeMeta = ROUTES[location.pathname] || null;
  const resolvedLd = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : (routeMeta?.jsonLd ? [...routeMeta.jsonLd] : []);
  if (routeMeta?.faq && Array.isArray(routeMeta.faq) && routeMeta.faq.length) {
    // Avoid double-adding if the caller already passed a FAQPage node.
    const hasFaq = resolvedLd.some(
      (n) => n && (n['@type'] === 'FAQPage' || (Array.isArray(n['@type']) && n['@type'].includes('FAQPage')))
    );
    if (!hasFaq) resolvedLd.push(faqPageLd(routeMeta.faq));
  }
  const jsonLdArray = resolvedLd;

  // Drop the pre-rendered static-HTML copies of these tags so React's
  // hoisted copies are the single source of truth after hydration.
  // Runs synchronously before paint on both first mount and every
  // route change (useLayoutEffect).
  useLayoutEffect(() => {
    cleanupStaticHead();
  }, [location.pathname]);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow'}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="AMP Predict" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <link rel="canonical" href={url} />
      <link
        rel="alternate"
        type="application/json"
        href={`${DEFAULT_BASE}/api/seo/answers.json`}
        title="AMP Predict — machine-readable Q&A for AI assistants"
      />
      {/* Publisher / AI answer engine hints. These signal to Perplexity,
          OpenAI SearchGPT, and other AI crawlers that we welcome citations
          and provide a stable JSON feed for grounding. */}
      <link
        rel="alternate"
        type="text/plain"
        href={`${DEFAULT_BASE}/api/robots.txt`}
        title="AMP Predict — canonical robots.txt"
      />
      <meta name="ai-content-declaration" content="permitted: search, ai-input, ai-train, reference" />
      <meta name="ai-answer-feed" content={`${DEFAULT_BASE}/api/seo/answers.json`} />
      <meta name="openai-searchgpt-permit" content="allow" />
      <meta name="perplexity-permit" content="allow" />
      <meta name="anthropic-permit" content="allow" />
      <meta name="google-ai-permit" content="allow" />

      {jsonLdArray.map((obj, i) => (
        <script
          key={`seo-jsonld-${i}`}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      ))}
    </>
  );
};

export default SEO;
